/** eslint-disable import/no-duplicates **/
import { MOBILE_BREAKPOINT_THRESHOLD } from '@cp/ds/src/theme';
import { Box } from '@cp/ds/src/components/box';
import { Button } from '@cp/ds/src/components/button';
import { Stack } from '@cp/ds/src/components/stack';
import { Image, VideoPlayer, VideoPlayerRef, styled } from '@youhelp-ru/ui';
import { Typography } from '@cp/ds/src/components/typography';
import { useTheme } from '@cp/ds/src/hooks/useTheme';
import { ArrowRightIcon } from '@cp/ds/src/icons/arrowRight';
import React, { useRef, useState } from 'react';
import { ModalView } from '@cp/ds/src/components/modalView';
import { PlayIcon } from '@cp/ds/src/icons';
import { useIsMobile } from '@cp/ds/src/hooks';
import { useAuth } from 'src/features/auth';
import VideoBannerImage from '../../../../../../../assets/images/homeVideoBanner.jpg';
import { BANNER_MIN_HEIGHT_DESKTOP } from '../../const';
import { BannerBase } from '../bannerBase';

const ModalRoot = styled(Stack)(({ theme }) => ({
  paddingInline: '16px',
  paddingTop: '32px',
  paddingBottom: '24px',
  backgroundColor: 'white',
  gap: '24px',
  [theme.breakpoints.up('md')]: {
    padding: '32px',
    gap: '40px',
  },
}));

const TEXT_WIDTH_DESKTOP = 330;

export const VideoBanner: React.FC = () => {
  const theme = useTheme();
  const isMobile = useIsMobile();
  const [bannerOpen, setOpen] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [shouldPreload, setShouldPreload] = useState(false);
  const login = useAuth();
  const playerRef = useRef<VideoPlayerRef>(null);

  const handlePlay = () => {
    setIsPlaying(!isPlaying);
    if (isPlaying) {
      playerRef.current?.pause();
    } else {
      playerRef.current?.play();
    }
  };

  return (
    <BannerBase
      sx={{
        position: 'relative',
        overflow: 'hidden',
        minHeight: { xs: 276, [MOBILE_BREAKPOINT_THRESHOLD]: BANNER_MIN_HEIGHT_DESKTOP },
        display: 'flex',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          zIndex: 9,
          maxWidth: { xs: '60%', [MOBILE_BREAKPOINT_THRESHOLD]: TEXT_WIDTH_DESKTOP },
          mr: { [MOBILE_BREAKPOINT_THRESHOLD]: 2 },
        }}
      >
        <Typography
          component="div"
          sx={{
            color: theme.palette.white,
            typography: { xs: 'h5', [MOBILE_BREAKPOINT_THRESHOLD]: 'h4' },
          }}
        >
          Школа для нянь YouHelp
        </Typography>
        <Typography
          component="div"
          sx={{
            color: theme.palette.white,
            mt: { xs: 2.5, [MOBILE_BREAKPOINT_THRESHOLD]: 2 },
            mb: { xs: 3.75, [MOBILE_BREAKPOINT_THRESHOLD]: 0 },
            typography: { xs: 'body bold', [MOBILE_BREAKPOINT_THRESHOLD]: 'h6' },
          }}
        >
          Мы учим, как выстраивать отношения с детьми и их родителями.
        </Typography>
        <Button
          color="gray"
          iconRight={<ArrowRightIcon />}
          onClick={() => {
            setShouldPreload(true);
            setOpen(true);
          }}
          size={isMobile ? 'small' : 'normal'}
          sx={{ mt: 'auto', whiteSpace: 'nowrap', backgroundColor: theme.palette.white }}
          variant="secondary"
        >
          Посмотреть
        </Button>
      </Box>

      <Image
        alt="Анкеты с видео"
        loading="lazy"
        src={VideoBannerImage}
        sx={{
          objectFit: 'cover',
          position: 'absolute',
          top: 0,
          left: 0,
          height: '100%',
          minHeight: '100%',
          width: 'auto',
          minWidth: '100%',
          zIndex: 1,
          [theme.breakpoints.down('cm')]: {
            left: '55%',
            transform: 'translateX(-50%)',
            objectPosition: '50% 50%',
          },
          [theme.breakpoints.between('cm', MOBILE_BREAKPOINT_THRESHOLD)]: {
            objectPosition: '50% 50%',
          },
          [theme.breakpoints.up(MOBILE_BREAKPOINT_THRESHOLD)]: {
            objectPosition: '50% 0px',
          },
        }}
      />

      <ModalView
        config={{ mobile: 'drawerBottom', desktop: 'dialog' }}
        isOpen={bannerOpen}
        onClose={() => {
          setOpen(false);
          setIsPlaying(false);
          playerRef.current?.pause();
        }}
        showCloseButton
      >
        <ModalRoot alignItems="center" justifyContent="center">
          <Typography component="div" variant="h1">
            Пример одного из блоков обучения:
            <Typography component="div" variant="button">
              которое проходят няни, опубликованные на нашей онлайн-платформе
            </Typography>
          </Typography>

          <Box
            onClick={handlePlay}
            sx={{
              position: 'relative',
              width: '100%',
              overflow: 'hidden',
              borderRadius: '8px',
            }}
          >
            <VideoPlayer
              controls
              controlsList="nodownload noremoteplayback noplaybackrate nofullscreen"
              disablePictureInPicture
              playsInline
              preload={shouldPreload ? 'metadata' : 'none'}
              ref={playerRef}
              src="https://cdn.youhelp.ru/training/lesson-1.mp4"
              sx={{
                width: '100%',
                opacity: isPlaying ? 'none' : 0.5,
                transition: 'opacity 0.3s ease',
              }}
              webkit-playsinline="true"
              x5-playsinline="true"
            />
            {!isPlaying && (
              <Box
                onClick={handlePlay}
                sx={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  zIndex: 999,
                  width: '50px',
                  height: '50px',
                  borderRadius: '50%',
                  color: 'white',
                  backgroundColor: theme.palette.text.primary,
                }}
              >
                <PlayIcon fontSize={'large'} />
              </Box>
            )}
          </Box>

          <Button
            color="gray"
            onClick={() => {
              setOpen(false);
              login();
            }}
            size="normal"
          >
            Вход и регистрация
          </Button>
        </ModalRoot>
      </ModalView>
    </BannerBase>
  );
};
